import { useMemo, useState, type ReactElement } from 'react';
import { Badge, Card, TabPanel, Tabs, type BadgeTone } from '@csa/ui';

/**
 * Showing an API response.
 *
 * Rendered as text, always. A response body from a target system is
 * untrusted input, and the one thing this view must never do is interpret
 * it -- no `dangerouslySetInnerHTML`, no iframe preview, no "render as
 * HTML" tab. A support tool that renders an arbitrary upstream response is
 * a stored-XSS delivery mechanism aimed at the people who hold the most
 * privilege in the estate.
 */

export interface ApiResult {
  readonly status?: number;
  readonly statusText?: string;
  readonly headers?: Record<string, string>;
  readonly body?: string;
  readonly durationMs?: number;
  readonly bytes?: number;
  readonly truncated?: boolean;
}

export function ResponseViewer({ result }: { result: ApiResult }): ReactElement {
  const [tab, setTab] = useState('body');
  const status = result.status ?? 0;
  const headerCount = Object.keys(result.headers ?? {}).length;

  return (
    <Card className="flex min-h-0 flex-col gap-3 p-3">
      <header className="flex flex-wrap items-center gap-3 text-sm">
        <Badge tone={toneFor(status)}>
          {status || '—'} {result.statusText ?? ''}
        </Badge>
        {result.durationMs !== undefined ? (
          <span className="text-muted">{result.durationMs.toLocaleString()} ms</span>
        ) : null}
        {result.bytes !== undefined ? (
          <span className="text-muted">{formatBytes(result.bytes)}</span>
        ) : null}
        {result.truncated ? <Badge tone="warning">truncated</Badge> : null}
      </header>

      <Tabs
        aria-label="Response parts"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'body', label: 'Body' },
          { value: 'headers', label: `Headers${headerCount ? ` (${headerCount})` : ''}` },
        ]}
      />

      <TabPanel value="body" active={tab}>
        <Body raw={result.body ?? ''} />
      </TabPanel>
      <TabPanel value="headers" active={tab}>
        <Headers headers={result.headers ?? {}} />
      </TabPanel>
    </Card>
  );
}

/**
 * Pretty-printing is offered, not imposed.
 *
 * When someone is debugging a mismatch between what a system returned and
 * what a parser expected, the exact bytes are the evidence -- reformatted
 * JSON has destroyed it. So reformatting is a toggle, and a body that does
 * not parse simply stays raw rather than showing a parse error the user did
 * not ask about.
 */
function Body({ raw }: { raw: string }): ReactElement {
  const [pretty, setPretty] = useState(true);

  const formatted = useMemo(() => {
    if (!pretty) return raw;
    try {
      return JSON.stringify(JSON.parse(raw), null, 2) as string;
    } catch {
      return raw;
    }
  }, [raw, pretty]);

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <label className="flex items-center gap-1.5 self-end text-xs text-muted">
        <input
          type="checkbox"
          className="size-4 accent-brand"
          checked={pretty}
          onChange={(event) => setPretty(event.target.checked)}
        />
        Format JSON
      </label>

      <pre className="max-h-96 overflow-auto rounded-md bg-sunken p-3 font-mono text-xs text-body">
        {formatted.length > 0 ? formatted : <span className="text-subtle">(empty body)</span>}
      </pre>
    </div>
  );
}

function Headers({ headers }: { headers: Record<string, string> }): ReactElement {
  const entries = Object.entries(headers).sort(([a], [b]) => a.localeCompare(b));

  if (entries.length === 0) return <p className="text-sm text-muted">No response headers.</p>;

  return (
    <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1 font-mono text-xs">
      {entries.map(([name, value]) => (
        <div key={name} className="contents">
          <dt className="truncate text-subtle">{name}</dt>
          <dd className="break-all text-body">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function toneFor(status: number): BadgeTone {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'neutral';
  if (status >= 400 && status < 500) return 'warning';
  if (status >= 500) return 'danger';
  return 'neutral';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
